import styled, { css } from "styled-components";

interface DropZoneProps {
  isDragging: boolean;
  hasPreview: boolean;
  isUploading: boolean;
}

export const UploadContainer = styled.div`
  display: grid;
  gap: 0.7rem;
  width: 100%;
  text-align: left;
`;

export const Label = styled.label`
  color: inherit;
  font-size: 0.95rem;
  font-weight: 700;
`;

export const DropZone = styled.button<DropZoneProps>`
  box-sizing: border-box;
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  min-height: ${(props) => (props.hasPreview ? "220px" : "176px")};
  margin: 0;
  padding: ${(props) => (props.hasPreview ? "0" : "1.5rem 1.25rem")};
  overflow: hidden;
  border: 2px dashed ${(props) => props.theme["purple-500"]};
  border-radius: 16px;
  background: ${(props) => props.theme.white};
  color: ${(props) => props.theme["gray-500"]};
  cursor: ${(props) => (props.isUploading ? "wait" : "pointer")};
  text-align: center;
  transition: border-color 0.2s ease, background-color 0.2s ease,
    box-shadow 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme["purple-500"]};
    outline-offset: 3px;
  }

  &:hover:not(:disabled) {
    border-color: ${(props) => props.theme["purple-700"]};
    background: ${(props) => props.theme["gray-50"]};
  }

  ${(props) =>
    props.isDragging &&
    css`
      border-style: solid;
      background: ${props.theme["gray-50"]};
      box-shadow: 0 0 0 4px ${props.theme["gray-50"]};
    `}

  &:disabled {
    cursor: wait;
  }
`;

export const EmptyState = styled.div`
  display: grid;
  justify-items: center;
  gap: 0.4rem;
`;

export const IconBadge = styled.span`
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  margin-bottom: 0.35rem;
  border-radius: 999px;
  background: ${(props) => props.theme["gray-50"]};
  color: ${(props) => props.theme["purple-500"]};
`;

export const DropTitle = styled.strong`
  font-size: 1rem;
  font-weight: 700;
`;

export const DropHint = styled.span`
  color: ${(props) => props.theme["gray-400"]};
  font-size: 0.875rem;
  line-height: 1.4;
`;

export const PreviewFrame = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  min-height: 220px;
  padding: 1rem;
  background: ${(props) => props.theme["gray-50"]};
`;

export const PreviewImage = styled.img`
  display: block;
  width: auto;
  max-width: 100%;
  max-height: 240px;
  border-radius: 12px;
  object-fit: contain;
`;

export const UploadingMask = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(10, 10, 11, 0.45);
  color: ${(props) => props.theme.white};
  font-size: 0.95rem;
  font-weight: 700;
`;

export const ProgressBlock = styled.div`
  display: grid;
  gap: 0.35rem;
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: ${(props) => props.theme["gray-50"]};
`;

export const ProgressValue = styled.div<{ $progress: number }>`
  width: ${(props) => props.$progress}%;
  height: 100%;
  border-radius: inherit;
  background: ${(props) => props.theme["purple-500"]};
  transition: width 0.2s ease;
`;

export const ProgressLabel = styled.span`
  color: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  opacity: 0.8;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const ActionButton = styled.button<{ $danger?: boolean }>`
  padding: 0.55rem 1rem;
  border: 1px solid
    ${(props) =>
      props.$danger ? props.theme["red-300"] : props.theme["purple-500"]};
  border-radius: 999px;
  background: ${(props) =>
    props.$danger ? "transparent" : props.theme["purple-500"]};
  color: ${(props) =>
    props.$danger ? props.theme["red-500"] : props.theme.white};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 700;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  &:hover:not(:disabled) {
    filter: brightness(0.96);
  }
`;
